import StatusChip from 'components/@extended/StatusChip';
import EmptyState from 'components/EmptyState';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import { DataTable, type DataTableColumnDef } from 'components/tables/data-table';
import { useListTableFilter } from 'components/tables/list-table/useListTableFilter';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetNewCasesQuery } from 'store/api/matters';
import { NewCaseListItem, NewCasesParams } from 'types/matters';
import { PaginationProps } from 'types/paginationAndFilter';
import { cn } from 'utils/cn';
import { PANEL_CLASS, ROOT_CLASS, SUMMARY_CLASS } from '../tables/MattersAccordionTable';

const ROWS_PER_PAGE = 25;
// About what a case row measures here (two-line description, people and referral lists), so the rows not
// measured yet start near their real height rather than at the engine's single-line seed.
const ROW_HEIGHT_ESTIMATE = 100;

const getCaseHref = (matter: NewCaseListItem) => `/matter/${matter.id}`;
// A row opens its case in a new tab, like its case link: a same-tab Back would remount every group collapsed.
const openCaseInNewTab = (href: string) => window.open(href, '_blank', 'noopener');

type Props = {
    title: ReactNode;
    caseCount: number;
    offerCount: number;
    params: NewCasesParams;
    /** The group's team id; absent for the cases in no group. */
    groupId?: number;
    columns: DataTableColumnDef<NewCaseListItem>[];
};

/**
 * One group of the NEW tab: its title and counts, and once expanded its cases in a bounded area that loads
 * the next page as it is scrolled. It starts collapsed, so the host collapses every group by remounting them.
 */
export function NewCasesGroupRow({ title, caseCount, offerCount, params, groupId, columns }: Props): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const [fetchCases, { data, isFetching, isLoading, isError, isUninitialized }] = useLazyGetNewCasesQuery();
    const fetchData = useCallback(
        (args: PaginationProps) =>
            fetchCases({
                ...args,
                extraParams: groupId === undefined ? params : { ...params, groupId: String(groupId) },
            }),
        [fetchCases, params, groupId]
    );
    const { dataToDisplay, handleLoadMore, handleNewSearch } = useListTableFilter<NewCaseListItem>({
        fetchData,
        data,
        rowsPerPage: ROWS_PER_PAGE,
        disableUrlState: true,
        canRememberFilters: false,
        disableFetch: !isOpen,
    });
    const isExpandable = caseCount > 0;
    const isNewDesign = useIsNewDesign();

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} disabled={!isExpandable} className={ROOT_CLASS}>
            <CollapsibleTrigger
                nativeButton={false}
                render={<div />}
                className={cn(
                    SUMMARY_CLASS,
                    'focus-visible:ring-ring/50 gap-2 outline-none focus-visible:ring-3 focus-visible:ring-inset',
                    isExpandable ? 'cursor-pointer' : 'cursor-default opacity-50'
                )}
            >
                <div className="flex grow items-center gap-1">{title}</div>
                {offerCount > 0 ? (
                    <StatusChip
                        label={<FormattedMessage id="matters.new-cases.offers-badge" values={{ count: offerCount }} />}
                    />
                ) : null}
                <ExpandMoreRoundedIcon
                    className={cn('text-fg-secondary transition-transform duration-150', isOpen && 'rotate-180')}
                />
            </CollapsibleTrigger>
            {/* The panel follows the table's height as it opens, so the table is mounted on open (a hidden table
                measures its rows at 0px), sized by its rows rather than by the panel, and draws a skeleton for
                the page not yet asked for. */}
            <CollapsibleContent className={PANEL_CLASS}>
                <div className="flex max-h-[600px] w-full flex-col">
                    <DataTable<NewCaseListItem>
                        columns={columns}
                        data={dataToDisplay}
                        getRowHref={isNewDesign ? getCaseHref : undefined}
                        onRowNavigate={openCaseInNewTab}
                        isLoading={isUninitialized || isFetching || isLoading}
                        skeletonRows={Math.min(caseCount, ROWS_PER_PAGE)}
                        estimatedRowHeight={ROW_HEIGHT_ESTIMATE}
                        className="h-auto! min-h-0 flex-1"
                        onLoadMore={handleLoadMore}
                        hasNextPage={!!data && !data.last}
                        isLoadingMore={isFetching || isLoading}
                        emptyState={
                            isError ? (
                                <EmptyState
                                    size="panel"
                                    variant="error"
                                    onRetry={() => handleNewSearch('')}
                                    surface="matters-new-cases-group"
                                />
                            ) : undefined
                        }
                    />
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}
